import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

/**
 * ProtectedRoute component that guards routes requiring authentication
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component to render if authenticated
 * @param {boolean} props.requireAdmin - If true, requires admin or subadmin role
 * @param {string[]} props.allowedRoles - Array of allowed admin roles (e.g., ['admin', 'subadmin'])
 */
const ProtectedRoute = ({ 
  children, 
  requireAdmin = false, 
  allowedRoles = ['admin', 'subadmin'],
  requiredPermission = null 
}) => {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px',
        color: '#666'
      }}>
        Loading...
      </div>
    );
  }

  // Check if user is authenticated
  if (!isAuthenticated) {
    // Redirect to home page with state to show login modal
    return <Navigate to="/" state={{ from: location, showLogin: true }} replace />;
  }

  // Check if admin access is required
  if (requireAdmin) {
    // Trust the context user role (validated against server on mount)
    const userRole = user?.adminRole || 'user';
    
    const hasAccess = allowedRoles.includes(userRole);
    
    if (!hasAccess) {
      // User is logged in but doesn't have admin access
      // Redirect to home page or show unauthorized message
      return <Navigate to="/" state={{ unauthorized: true }} replace />;
    }
  }

  // Check granular permission for sub-admins
  if (requiredPermission) {
    const currentRole = user?.adminRole || 'user';
    // Admins bypass permission checks; subadmins must have the specific permission
    if (currentRole === 'subadmin' && user[requiredPermission] !== true) {
      return <Navigate to="/admin/dashboard" state={{ permissionDenied: true }} replace />;
    }
  }

  // User is authenticated and has required access
  return children;
};

export default ProtectedRoute;
